import apiClient from '../lib/apiClient';
import generateIdempotencyKey from './idempotency';
import { openRazorpayCheckout } from '../lib/razorpay';

const unwrap = (response) => (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')
  ? response.data
  : response);

const digitsOnly = (value = '') => String(value || '').replace(/\D+/g, '');

const CHECKOUT_CONFIG_ID = 'config_RWSHp92Dxw1LTj';
const CORPUSIO_CHECKOUT_COLOR = '#3457D5';
const METHOD_PREFS = {
    card: { card: true, netbanking: false, wallet: false, emi: false, upi: false, paylater: false },
    upi:  { card: false, netbanking: false, wallet: false, emi: false, upi: true, paylater: false },
};

const getRuntimeConfig = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  return window.__CB_CONFIG__ || window.__PUBLIC_CONFIG__ || {};
};

const normalizeCountry = (value, fallback = 'IN') => {
  if (!value) return fallback;
  return String(value).trim() || fallback;
};

const phoneVerificationRequired = (err = {}) => (
  err?.requiresPhoneVerification
  || err?.response?.data?.requiresphoneverification
  || err?.response?.data?.requires_phone_verification
  || err?.response?.data?.requiresPhoneVerification
);

const raisePhoneVerificationError = (err) => {
  if (phoneVerificationRequired(err)) {
    const message = err?.response?.data?.error || err?.message || 'Please verify your phone number before continuing.';
    const error = new Error(message);
    error.requiresPhoneVerification = true;
    throw error;
  }
};

const emailVerificationRequired = (err = {}) => (
  err?.requiresEmailVerification
  || err?.response?.data?.requiresemailverification
  || err?.response?.data?.requires_email_verification
);

const raiseEmailVerificationError = (err) => {
  if (emailVerificationRequired(err)) {
    const message = err?.response?.data?.error || err?.message || 'Please verify your email address before continuing.';
    const error = new Error(message);
    error.requiresEmailVerification = true;
    throw error;
  }
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const cleanupUnpaidRetryOrder = async (orderId) => {
  if (!orderId) return;
  try {
    await apiClient.post(`/api/orders/${orderId}/delete`, {});
  } catch (err) {
    console.warn('Unable to clean up cancelled retry order', orderId, err);
  }
};

export async function retryOrderPayment(orderId, user = {}, options = {}, method = '') {
  if (!orderId) {
    throw new Error('Order ID is required to retry payment.');
  }

  const orderDetailsResp = await apiClient.get(`/api/orders/${orderId}/details`);
  const orderDetails = unwrap(orderDetailsResp);
  if (!orderDetails || !orderDetails.product) {
    throw new Error('Unable to load order details for retry.');
  }

  const normalizedStatus = String(orderDetails.status || '').toLowerCase();
  const reauthorizationRequired = Boolean(
    orderDetails.reauthorization_required || normalizedStatus === 'reauthorization_required'
  );
  const hasPayment = Boolean(orderDetails.payment_id || orderDetails.paymentid);
  const paidStatuses = ['paid', 'captured', 'success', 'completed'];
  if (!reauthorizationRequired && (hasPayment || paidStatuses.includes(normalizedStatus))) {
    throw new Error('This order has already been paid. Refresh your dashboard to view the updated status.');
  }
  if (normalizedStatus && !['created', 'failed', 'pending', 'attempted', 'reauthorization_required'].includes(normalizedStatus)) {
    throw new Error(`This order cannot be retried while it is ${normalizedStatus}. Refresh your dashboard to view the updated status.`);
  }

  const customerEmail = (user.email || orderDetails.customer_email || '').trim();
  if (!customerEmail) {
    throw new Error('Profile email missing. Please update your profile before retrying payment.');
  }

  const phoneDigits = digitsOnly(user.phone || orderDetails.customer_contact || '');
  if (!phoneDigits) {
    throw new Error('Profile phone number missing. Please update your phone number before retrying payment.');
  }

  const customerName = (user.name || orderDetails.customer_name || 'Corpusio Customer').trim();
  const organization = user.organizationName || user.organizationname || '';
  const phoneCountry = normalizeCountry(user.phoneCountry || user.phonecountry || 'IN').toUpperCase();

  const timestamp = Date.now();
  const invoiceNumber = `INV-RETRY-${timestamp}-${Math.floor(Math.random() * 1e6)}`;
  const retryOrderKey = generateIdempotencyKey(`retry-order-${orderId}-${timestamp}`);
  const verifyKey = generateIdempotencyKey(`verify-retry-${orderId}-${timestamp}`);

  const normalizedMethod = ['card', 'upi'].includes(method) ? method : '';
  const subscriptionPayload = {
    product: orderDetails.product,
    customer_id: orderDetails.customer_id,
    frequency: 'as_presented',
    receipt: invoiceNumber,
    notes: {
      invoice_number: invoiceNumber,
      product_key: orderDetails.product,
      retry_for_order: orderId,
      retry_attempt: 'true',
    },
  };
  if (normalizedMethod) {
    subscriptionPayload.method = normalizedMethod;
  }
  const retryOrderResp = await apiClient.post('/api/create_subscription', subscriptionPayload, {
    headers: { 'Idempotency-Key': retryOrderKey },
  });
  const retryOrder = unwrap(retryOrderResp);
  const retryOrderId = retryOrder.order_id || retryOrder.id;
  if (!retryOrderId) {
    throw new Error('Unable to create a fresh retry order.');
  }

  const runtimeConfig = getRuntimeConfig();
  const keyId = retryOrder.key_id
    || orderDetails.key_id
    || runtimeConfig.razorpayKeyId
    || runtimeConfig.RAZORPAY_KEY_ID;
  if (!keyId) {
    throw new Error('Razorpay key is not configured.');
  }

  const logoUrl = runtimeConfig.brandLogoUrl || '/corpusio-logo.webp';
  let payment;
  try {
    payment = await openRazorpayCheckout({
      key: keyId,
      amount: retryOrder.amount || orderDetails.amount,
      currency: retryOrder.currency || orderDetails.currency || 'INR',
      name: 'Corpusio',
      description: options.productName
        ? `Retry payment · ${options.productName}`
        : 'Retry payment · Corpusio workspace',
      image: logoUrl,
      orderId: String(retryOrderId),
      customerId: orderDetails.customer_id,
      method: normalizedMethod ? METHOD_PREFS[normalizedMethod] : undefined,
      recurring: retryOrder.recurring || 'preferred',
      notes: {
        retry_origin: 'dashboard',
        original_order_id: orderId,
        retry_order_id: retryOrderId,
        invoice_number: invoiceNumber,
      },
      prefill: {
        name: customerName,
        email: customerEmail,
        contact: phoneDigits,
      },
      theme: { color: CORPUSIO_CHECKOUT_COLOR },
      retry: { enabled: true, max_count: 1 },
    });
  } catch (err) {
    await cleanupUnpaidRetryOrder(retryOrderId);
    throw err;
  }

  const verification = new FormData();
  verification.append('razorpay_payment_id', payment.razorpay_payment_id);
  verification.append('razorpay_order_id', payment.razorpay_order_id || retryOrderId);
  verification.append('razorpay_signature', payment.razorpay_signature);
  verification.append('product', orderDetails.product);
  verification.append('invoice_number', invoiceNumber);
  verification.append('retry_for_order', orderId);
  verification.append('workspaceName', organization || customerName || 'Corpusio Workspace');
  verification.append('dataConsent', '1');
  verification.append('consentVersion', 'corpusio-workspace-terms-v1');
  verification.append('consentText', 'Corpusio workspace data & usage terms accepted');
  verification.append('consentSignedAt', new Date().toISOString());
  const tz = getTimezone();
  if (tz) {
    verification.append('consentTimezone', tz);
  }
  verification.append('yourPhone', phoneDigits);
  verification.append('yourPhoneCountry', phoneCountry);
  if (options.note) {
    verification.append('specialNote', options.note);
  }

  try {
    await apiClient.post('/api/verify_payment', verification, {
      headers: { 'Idempotency-Key': verifyKey },
    });
  } catch (err) {
    raisePhoneVerificationError(err);
    raiseEmailVerificationError(err);
    throw err;
  }

  return { success: true };
}

export default retryOrderPayment;
